import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { collection, onSnapshot, doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase';
import { HomeContent, Advertisement, PaymentRequest as PaymentRequestType } from '../types';

interface AppContextType {
  // Shared data
  homeContent: HomeContent | null;
  advertisements: Advertisement[];
  paymentRequests: PaymentRequestType[];

  // Loading states
  loading: {
    homeContent: boolean;
    advertisements: boolean;
    paymentRequests: boolean;
  };

  // Actions
  incrementAdView: (adId: string) => Promise<void>;
  refreshPaymentRequests: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [homeContent, setHomeContent] = useState<HomeContent | null>(null);
  const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
  const [paymentRequests, setPaymentRequests] = useState<PaymentRequestType[]>([]);

  const [loading, setLoading] = useState({
    homeContent: true,
    advertisements: true,
    paymentRequests: true
  });

  // Default content
  const defaultContent: HomeContent = {
    companyName: 'SciPaints',
    tagline: 'Premium Paint Solutions',
    description: 'Leading manufacturer of high-quality paints and coatings for residential and commercial applications.',
    features: [
      { title: 'Premium Quality', description: 'Advanced formulations with superior coverage and durability', icon: 'StarIcon' },
      { title: 'Eco-Friendly', description: 'Low VOC and environmentally conscious paint solutions', icon: 'ShieldCheckIcon' },
      { title: 'Quick Drying', description: 'Fast-drying formulas for efficient project completion', icon: 'TruckIcon' },
      { title: 'Long Warranty', description: 'Extended warranty protection for peace of mind', icon: 'CheckCircleIcon' }
    ],
    testimonials: [
      { name: 'Rajesh Kumar', text: 'SciPaint transformed my home with their premium quality paints. The colors are vibrant and long-lasting!', rating: 5, role: 'Homeowner' },
      { name: 'Priya Sharma', text: 'I trust SciPaint for all my projects. Their color palette and finish quality are unmatched.', rating: 5, role: 'Interior Designer' },
      { name: 'Amit Patel', text: 'Best paint brand I\'ve worked with. Easy application and excellent support.', rating: 5, role: 'Contractor' }
    ],
    contactInfo: {
      phone: '+91 1800-SCIPAINT',
      email: 'info@scipaint.com',
      address: '123 Paint Street, Mumbai, Maharashtra',
      website: 'www.scipaint.com'
    },
    specialOffers: [
      { title: 'New Member Discount', description: 'Get 10% off on your first order!', validUntil: '2024-12-31' },
      { title: 'Bulk Order Special', description: 'Special rates for bulk orders above ₹5000', validUntil: '2024-12-31' }
    ]
  };

  // Home Content Listener (Global - shared across all pages)
  useEffect(() => {
    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      setHomeContent(defaultContent);
      setLoading(prev => ({ ...prev, homeContent: false }));
    }, 3000); // 3 second timeout

    const unsubscribe = onSnapshot(
      collection(db, 'homeContent'),
      (snapshot) => {
        clearTimeout(timeout); // Clear timeout on successful load
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          const data = doc.data() as any;

          const processedData = {
            companyName: data.companyName || defaultContent.companyName,
            tagline: data.companyTagline || data.tagline || defaultContent.tagline,
            description: data.companyDescription || data.description || defaultContent.description,
            features: data.features || defaultContent.features,
            testimonials: data.testimonials || defaultContent.testimonials,
            contactInfo: {
              phone: data.phone || defaultContent.contactInfo.phone,
              email: data.email || defaultContent.contactInfo.email,
              address: data.address || defaultContent.contactInfo.address,
              website: data.website || defaultContent.contactInfo.website
            },
            specialOffers: data.specialOffers || defaultContent.specialOffers,
            updatedAt: data.updatedAt?.toDate() || new Date()
          };

          setHomeContent(processedData);
        } else {
          setHomeContent(defaultContent);
        }
        setLoading(prev => ({ ...prev, homeContent: false }));
      },
      (error) => {
        clearTimeout(timeout); // Clear timeout on error
        console.error('Home content loading error:', error);
        setHomeContent(defaultContent);
        setLoading(prev => ({ ...prev, homeContent: false }));
      }
    );

    return () => {
      clearTimeout(timeout);
      unsubscribe();
    };
  }, []);

  // Advertisements Listener (Global - shared across all pages)
  useEffect(() => {
    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      setAdvertisements([]);
      setLoading(prev => ({ ...prev, advertisements: false }));
    }, 3000); // 3 second timeout

    const unsubscribe = onSnapshot(
      collection(db, 'advertisements'),
      (snapshot) => {
        clearTimeout(timeout); // Clear timeout on successful load
        const ads = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Advertisement)).filter(ad =>
          ad.isActive &&
          (!ad.targetApp || ad.targetApp === 'painter' || ad.targetApp === 'both')
        );
        setAdvertisements(ads);
        setLoading(prev => ({ ...prev, advertisements: false }));
      },
      (error) => {
        clearTimeout(timeout); // Clear timeout on error
        console.error('Advertisements loading error:', error);
        setAdvertisements([]);
        setLoading(prev => ({ ...prev, advertisements: false }));
      }
    );

    return () => {
      clearTimeout(timeout);
      unsubscribe();
    };
  }, []);

  const incrementAdView = async (adId: string) => {
    try {
      const adRef = doc(db, 'advertisements', adId);
      await updateDoc(adRef, {
        viewCount: increment(1)
      });
    } catch (error) {
      // Silently fail
    }
  };

  const refreshPaymentRequests = () => { };

  const value: AppContextType = {
    homeContent,
    advertisements,
    paymentRequests,
    loading,
    incrementAdView,
    refreshPaymentRequests
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}; 